import React, { useCallback, FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Field } from 'formik';
import * as yup from 'yup';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_green.css';
import { format, addDays, addHours, setMinutes } from 'date-fns';
import { earliestPwaAppointmentString } from './earliestPwaAppointment';
import { db } from '../../../firebase';
import { AuthContext } from '../../../Providers/UserProvider';
import LoaderComponent from '../../../Shared/Loader';
import { rejectionMessage } from '../../../utils/rejectionMessage';
import { MISSING_COMPANY_CLAIM_ERROR, companyIdFromClaims } from '../../../utils/companyClaim';
import { getConfirmation } from '../../../utils/ui';
import useJobSheetDialog from '../../../utils/useJobSheetDialog';
import StatusEmptyCard from '../../StatusPage/StatusEmptyCard';
import {
    CREATE_FIXTURE_NOTICE,
    CREATE_LIVE_EMPTY_NOTICE,
    CREATE_PWA_JOB_CONFIRM,
    CREATE_PWA_JOB_CONFIRM_TITLE,
    PWA_CREATE_SOT_VALUES,
    TRUE_EMPTY_WALK_CONFIRM,
    TRUE_EMPTY_WALK_CONFIRM_TITLE,
    createPwaJobFromForm,
    isTrueEmptyWalkFixture,
    pwaCreateCopy,
    pwaCreateFixtureDetail,
    pwaCreateGuard,
    pwaCreatePath,
    pwaCreateWriteError,
} from '../createPwaJob';

function loadAlertMessage(error: unknown, fallback: string): string {
    const detail = rejectionMessage(error, '');
    return detail ? `${fallback} ${detail}` : fallback;
}

interface Props {
    onClose: () => void;
    onCreated?: (result?: { jobId?: string; status?: string }) => void;
    fixture?: string | null;
    fixtureSource?: string | null;
    liveEmpty?: boolean;
    offline?: boolean;
    createJob?: typeof createPwaJobFromForm;
    confirmCreate?: typeof getConfirmation;
}

const requiredCreateText = (label: string) => yup.string().trim().required(label);

/** Trim before required(), so whitespace-only make/model/insurance/shop/contact/name fails closed. */
export const orderSchema = yup.object({
    contact: requiredCreateText('This input is required'),
    customerName: requiredCreateText('This input is required'),
    type: yup.string().required('This input is required'),
    damage: yup.string().required('This input is required'),
    date: yup.string().required('This input is required'),
    vehicle: yup.object({
        year: yup.number().required('This input is required'),
        make: requiredCreateText('This input is required'),
        model: requiredCreateText('This input is required'),
        insurance: requiredCreateText('This input is required'),
    }),
    shop: requiredCreateText('This input is required'),
});

function trimmedCreateString(value: unknown): unknown {
    return typeof value === 'string' ? value.trim() : value;
}

/** Values handed to the create write. Required strings are already trimmed. */
export function trimCreateJobValues<T extends Record<string, unknown>>(values: T): T {
    const vehicle =
        values.vehicle && typeof values.vehicle === 'object' ? (values.vehicle as Record<string, unknown>) : undefined;
    return {
        ...values,
        contact: trimmedCreateString(values.contact),
        customerName: trimmedCreateString(values.customerName),
        shop: trimmedCreateString(values.shop),
        vehicle: vehicle
            ? {
                  ...vehicle,
                  make: trimmedCreateString(vehicle.make),
                  model: trimmedCreateString(vehicle.model),
                  insurance: trimmedCreateString(vehicle.insurance),
              }
            : values.vehicle,
    };
}

const CreateJobModal: FunctionComponent<Props> = ({
    onClose,
    onCreated,
    fixture = null,
    fixtureSource = null,
    liveEmpty = false,
    offline = false,
    createJob = createPwaJobFromForm,
    confirmCreate = getConfirmation,
}) => {
    const { shopsRef, collectionPrefix, techId, user } = useContext(AuthContext);

    const openingGuard = useMemo(
        () =>
            pwaCreateGuard({
                fixture,
                offline,
                techId,
                collectionPrefix,
            }),
        [fixture, offline, techId, collectionPrefix],
    );
    const [surface, setSurface] = useState(
        openingGuard && openingGuard !== 'sot' && openingGuard !== 'preview' ? openingGuard : null,
    );
    const [createError, setCreateError] = useState('');
    const [createdResult, setCreatedResult] = useState<{ jobId?: string; status?: string } | null>(null);
    const [busy, setBusy] = useState(false);
    /** Synchronous click lock. `busy` flips only after confirm, too late to ignore a second click. */
    const createLock = useRef(false);
    /**
     * One jobs doc id for every attempt. The error card clears `surface` and remounts the form;
     * this modal stays mounted, so a timed-out set retries the same document.
     */
    const pendingJobId = useRef<string | null>(null);
    const [createLocked, setCreateLocked] = useState(false);
    /** True once submitCreate starts, so a validation miss can release the lock without cutting off confirm. */
    const createSubmitted = useRef(false);
    const [validatingRealTime, setValidatingRealTime] = useState(false);
    const [locations, setLocations] = useState<any>([]);
    const [showLoader, setShowLoader] = useState(false);
    const [shopsError, setShopsError] = useState('');

    const copy = pwaCreateCopy(surface);
    const fixtureNotice = CREATE_FIXTURE_NOTICE[fixture || ''] || (liveEmpty ? CREATE_LIVE_EMPTY_NOTICE : null);
    const createSotFixture = (PWA_CREATE_SOT_VALUES as readonly string[]).includes(fixture || '');
    const walkFixture = isTrueEmptyWalkFixture(fixture);
    const fixtureDetail =
        createSotFixture || walkFixture ? pwaCreateFixtureDetail(walkFixture ? fixture : fixtureSource) : undefined;
    const createPath = pwaCreatePath(fixture, liveEmpty);

    const closeModalRef = useRef<() => void>(() => undefined);
    const { dialogRef, backButtonRef, restoreFocus } = useJobSheetDialog(() => {
        closeModalRef.current();
    });

    const closeModal = useCallback(() => {
        pendingJobId.current = null;
        restoreFocus();
        onClose();
    }, [onClose, restoreFocus]);
    closeModalRef.current = closeModal;

    const finishCreated = (result = createdResult) => {
        if (onCreated) onCreated(result || undefined);
        else onClose();
    };

    const retryOffline = () => {
        const stillOffline = typeof navigator !== 'undefined' ? navigator.onLine === false : false;
        if (stillOffline) {
            setSurface('offline');
            return;
        }
        const next = pwaCreateGuard({
            fixture,
            offline: false,
            techId,
            collectionPrefix,
        });
        setSurface(next === 'denied' || next === 'fixture-region' ? next : null);
    };

    const releaseCreateLock = () => {
        createLock.current = false;
        setCreateLocked(false);
    };

    const jobIdForAttempt = () => {
        if (!pendingJobId.current) {
            pendingJobId.current = db.collection(`${collectionPrefix}/jobs`).doc().id;
        }
        return pendingJobId.current;
    };

    const submitCreate = async (values: Record<string, unknown>) => {
        createSubmitted.current = true;
        try {
            const guard = pwaCreateGuard({
                fixture,
                offline,
                techId,
                collectionPrefix,
            });
            if (guard) {
                setSurface(guard);
                return;
            }
            if (walkFixture) {
                const confirmedWalk = await confirmCreate(TRUE_EMPTY_WALK_CONFIRM_TITLE, TRUE_EMPTY_WALK_CONFIRM, {
                    confirmButtonText: 'Show confirm step',
                    cancelButtonText: 'Cancel',
                    showCancelButton: true,
                });
                if (!confirmedWalk) {
                    setCreateError('');
                    setSurface('cancelled');
                    return;
                }
                setSurface(fixture);
                return;
            }
            const confirmed = await confirmCreate(CREATE_PWA_JOB_CONFIRM_TITLE, CREATE_PWA_JOB_CONFIRM, {
                confirmButtonText: 'Create pending job',
                cancelButtonText: 'Cancel',
                showCancelButton: true,
            });
            if (!confirmed) {
                setCreateError('');
                setSurface('cancelled');
                return;
            }
            setBusy(true);
            setCreateError('');
            try {
                const result = await createJob(trimCreateJobValues(values), {
                    techId,
                    collectionPrefix,
                    jobId: jobIdForAttempt(),
                });
                pendingJobId.current = null;
                setSurface('pending');
                setCreateError(result.jobId ? `Job ${result.jobId} is pending.` : '');
                setCreatedResult(result);
            } catch (error) {
                setSurface('error');
                setCreateError(pwaCreateWriteError(error as { message?: string }));
            } finally {
                setBusy(false);
            }
        } finally {
            releaseCreateLock();
        }
    };

    const damageTypeOptions = [
        {
            value: 'Front',
            title: 'Front',
            description: 'This is a front job.',
        },
        {
            value: 'Rear',
            title: 'Rear',
            description: 'This is a rear job.',
        },
        {
            value: '1/4 Glass',
            title: '1/4 Glass',
            description: 'This is a 1/4 Glass job.',
        },
        {
            value: 'Other',
            title: 'Other',
            description: 'This is another type of job.',
        },
    ];

    const jobTypeOptions = [
        {
            value: 'Remove',
            title: 'Remove',
            description: 'This job is a removal.',
        },
        {
            value: 'Install',
            title: 'Install',
            description: 'This job is an installation.',
        },
        {
            value: 'Remove & Install',
            title: 'Remove & Install',
            description: 'This is a removal and installation.',
        },
        {
            value: 'Cliff test',
            title: 'Cliff test',
            description: 'Test job type for Glass milestone verification.',
        },
    ];

    const dropoff = [
        {
            value: 'Currently dropped',
            title: 'Currently dropped',
        },
        {
            value: 'Will be dropped',
            title: 'Will be dropped',
        },
        {
            value: 'Not dropping',
            title: 'Not dropping',
        },
    ];

    useEffect(() => {
        const companyId = companyIdFromClaims((user as { claims?: { companyId?: unknown } } | null)?.claims);
        if (fixture || !shopsRef || !companyId) {
            setLocations(fixture ? [{ id: 'sot-shop', name: 'SoT shop (paint-only)' }] : []);
            setShopsError(!fixture && shopsRef && !companyId ? MISSING_COMPANY_CLAIM_ERROR : '');
            setShowLoader(false);
            return undefined;
        }
        setShowLoader(true);
        setShopsError('');
        return shopsRef.where('companyId', '==', companyId).onSnapshot(
            (querySnapshot) => {
                const arrayShops: any[] = [];
                querySnapshot.forEach((documentSnapshot) => {
                    let newShops = documentSnapshot.data();
                    newShops = { ...newShops, id: documentSnapshot.id };
                    arrayShops.push(newShops);
                });
                setLocations(arrayShops);
                setShopsError('');
                setShowLoader(false);
            },
            (error) => {
                console.error('Unable to load shop locations', error);
                setLocations([]);
                setShopsError(loadAlertMessage(error, 'Shop locations could not load.'));
                setShowLoader(false);
            },
        );
    }, [shopsRef, fixture, user]);

    const handleCalendarChange = (formik, [date]) => {
        if (!date) {
            formik.setFieldValue('date', '');
            return;
        }
        const dateValue = format(date, 'yyyy-MM-dd HH:mm');
        formik.setFieldValue('date', dateValue);
    };

    const getMinDate = () => earliestPwaAppointmentString();

    return (
        <>
            <LoaderComponent showLoader={showLoader || busy} />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label="Add Job"
                tabIndex={-1}
                className="fixed w-full h-full top-0 left-0 flex z-50 "
                data-testid="create-job-modal"
                data-create-surface={surface || 'form'}
                data-create-path={createPath}
            >
                <div
                    className="fixed w-full overflow-y-scroll bg-details-background bg-cover pb-12"
                    // eslint-disable-next-line react-native/no-inline-styles
                    style={{ height: '100%' }}
                >
                    <div className="fixed justify-start p-2 border-b border-solid border-gray-300 bg-gray-100 w-full">
                        <button
                            ref={backButtonRef}
                            className="text-blue-500 background-transparent font-bold text-sm outline-none focus:outline-none"
                            type="button"
                            onClick={closeModal}
                        >
                            <FontAwesomeIcon className="text-base mr-1" icon={faChevronLeft} />
                            <span>Back</span>
                        </button>
                    </div>
                    <div className="pt-14 container mx-auto h-auto pb-4 text-center">
                        {copy ? (
                            <div className="flex flex-col items-center mt-6">
                                <StatusEmptyCard
                                    copy={copy}
                                    detail={
                                        surface === 'error' || surface === 'pending'
                                            ? createError || fixtureDetail
                                            : fixtureDetail
                                    }
                                    onAction={
                                        surface === 'offline'
                                            ? retryOffline
                                            : surface === 'error' || surface === 'cancelled'
                                            ? () => {
                                                  setCreateError('');
                                                  setSurface(null);
                                              }
                                            : surface === 'pending'
                                            ? () => finishCreated()
                                            : surface === 'denied' ||
                                              surface === 'fixture-region' ||
                                              surface === 'preview'
                                            ? closeModal
                                            : undefined
                                    }
                                />
                            </div>
                        ) : (
                            <>
                                <div className="text-lg font-medium px-3">Add Job</div>
                                {shopsError ? (
                                    <p
                                        className="text-red-600 text-sm mt-2 px-4"
                                        role="alert"
                                        data-testid="create-job-shops-error"
                                    >
                                        {shopsError}
                                    </p>
                                ) : null}
                                {fixtureNotice ? (
                                    <p className="text-sm text-gray-600 mt-2 px-4" data-testid="create-fixture-notice">
                                        {fixtureNotice}
                                    </p>
                                ) : null}
                                <Formik
                                    initialValues={{
                                        type: '',
                                        damage: '',
                                        date: format(
                                            addHours(setMinutes(addDays(new Date(), 1), 0), 1),
                                            'yyyy-MM-dd HH:mm',
                                        ),
                                        shop: '',
                                        customerName: '',
                                        contact: '',
                                        vehicle: {
                                            year: '',
                                            make: '',
                                            model: '',
                                            insurance: '',
                                        },
                                        assignee: techId,
                                        notes: '',
                                        status: 'pending',
                                        invoiced: false,
                                        waiting: false,
                                        dropped: 'Currently dropped',
                                    }}
                                    validateOnBlur={false}
                                    validateOnChange={validatingRealTime}
                                    onSubmit={submitCreate}
                                    validationSchema={orderSchema}
                                >
                                    {(formik) => (
                                        <>
                                            <div>
                                                <div className="my-4">
                                                    <select
                                                        required
                                                        aria-label="Job Type"
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                        value={formik.values.type || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('type', event.target.value)
                                                        }
                                                    >
                                                        <option className="placeholder-gray-600" value="">
                                                            Select job type
                                                        </option>
                                                        {jobTypeOptions.map((type) => (
                                                            <option key={type.value} value={type.value}>
                                                                {type.title}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {formik.errors.type && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.type}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <select
                                                        required
                                                        aria-label="Service Type"
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                        value={formik.values.damage || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('damage', event.target.value)
                                                        }
                                                    >
                                                        <option className="placeholder-gray-600" value="">
                                                            Select service type
                                                        </option>
                                                        {damageTypeOptions.map((damageType) => (
                                                            <option key={damageType.value} value={damageType.value}>
                                                                {damageType.title}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {formik.errors.damage && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.damage}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <Flatpickr
                                                        data-enable-time
                                                        value={formik.values.date}
                                                        options={{
                                                            minuteIncrement: 15,
                                                            altInput: true,
                                                            altFormat: 'M j\\, Y h\\:i K',
                                                            dateFormat: 'Y\\-m\\-d\\ H\\:i',
                                                            minDate: getMinDate(),
                                                        }}
                                                        onChange={(event) => handleCalendarChange(formik, event)}
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                        name="date"
                                                    />
                                                    {formik.errors.date && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.date}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <select
                                                        required
                                                        aria-label="Shop"
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                        value={formik.values.shop || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('shop', event.target.value)
                                                        }
                                                    >
                                                        <option className="placeholder-gray-600" value="">
                                                            Select Shop
                                                        </option>
                                                        {locations.map((shopInfo) => (
                                                            <option key={shopInfo.id} value={shopInfo.id}>
                                                                {shopInfo.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {formik.errors.shop && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.shop}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <input
                                                        required
                                                        type="text"
                                                        name="customerName"
                                                        id="customerName"
                                                        aria-label="Customer Name"
                                                        placeholder="Customer Name"
                                                        value={formik.values.customerName || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('customerName', event.target.value)
                                                        }
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                    />
                                                    {formik.errors.customerName && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.customerName}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <input
                                                        required
                                                        type="text"
                                                        name="contact"
                                                        id="contact"
                                                        aria-label="Primary Contact"
                                                        placeholder="Primary Contact"
                                                        value={formik.values.contact || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('contact', event.target.value)
                                                        }
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                    />
                                                    {formik.errors.contact && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.contact}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4 text-left mx-auto px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72">
                                                    <span>
                                                        Available to pick up:
                                                        <Field className="ml-3" type="checkbox" name="waiting" />
                                                    </span>
                                                </div>
                                                <div className="my-4">
                                                    <input
                                                        type="text"
                                                        name="year"
                                                        id="year"
                                                        aria-label="Vehicle Year"
                                                        placeholder="Vehicle Year"
                                                        value={formik.values.vehicle.year || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('vehicle.year', event.target.value)
                                                        }
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                    />
                                                    {formik.errors.vehicle?.year && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.vehicle.year}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <input
                                                        type="text"
                                                        name="make"
                                                        id="make"
                                                        aria-label="Vehicle Make"
                                                        placeholder="Vehicle Make"
                                                        value={formik.values.vehicle.make || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('vehicle.make', event.target.value)
                                                        }
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                    />
                                                    {formik.errors.vehicle?.make && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.vehicle.make}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <input
                                                        type="text"
                                                        name="model"
                                                        id="model"
                                                        aria-label="Vehicle Model"
                                                        placeholder="Vehicle Model"
                                                        value={formik.values.vehicle.model || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('vehicle.model', event.target.value)
                                                        }
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                    />
                                                    {formik.errors.vehicle?.model && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.vehicle.model}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <input
                                                        type="text"
                                                        name="insurance"
                                                        id="insurance"
                                                        aria-label="Vehicle Insurance"
                                                        placeholder="Vehicle Insurance"
                                                        value={formik.values.vehicle.insurance || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue(
                                                                'vehicle.insurance',
                                                                event.target.value,
                                                            )
                                                        }
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                    />
                                                    {formik.errors.vehicle?.insurance && (
                                                        <p className="text-red-500 text-xs italic mt-2">
                                                            {formik.errors.vehicle.insurance}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="my-4">
                                                    <select
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                        value={formik.values.dropped || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('dropped', event.target.value)
                                                        }
                                                    >
                                                        <option className="placeholder-gray-600" value="">
                                                            Vehicle Dropoff
                                                        </option>
                                                        {dropoff.map((dropoffType) => (
                                                            <option key={dropoffType.value} value={dropoffType.value}>
                                                                {dropoffType.title}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="my-4">
                                                    <input
                                                        type="text"
                                                        name="notes"
                                                        id="notes"
                                                        aria-label="Notes"
                                                        placeholder="Order Notes"
                                                        value={formik.values.notes || ''}
                                                        onChange={(event) =>
                                                            formik.setFieldValue('notes', event.target.value)
                                                        }
                                                        className="px-3 py-3 placeholder-gray-600 text-gray-800 bg-gray-100 rounded focus:shadow-outline focus:outline-none w-72"
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="bg-green-400 hover:bg-green-900 text-white font-bold py-6 px-4 w-full"
                                                disabled={createLock.current || createLocked || busy}
                                                onClick={() => {
                                                    if (createLock.current) return;
                                                    const guard = pwaCreateGuard({
                                                        fixture,
                                                        offline,
                                                        techId,
                                                        collectionPrefix,
                                                    });
                                                    if (guard) {
                                                        setSurface(guard);
                                                        return;
                                                    }
                                                    createLock.current = true;
                                                    setCreateLocked(true);
                                                    createSubmitted.current = false;
                                                    setValidatingRealTime(true);
                                                    void Promise.resolve(formik.submitForm()).finally(() => {
                                                        // Validation never enters submitCreate. Leave the lock to
                                                        // submitCreate's finally once that function has started.
                                                        if (!createSubmitted.current) releaseCreateLock();
                                                    });
                                                }}
                                            >
                                                {busy ? 'Creating…' : 'Create Job'}
                                            </button>
                                        </>
                                    )}
                                </Formik>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreateJobModal;
